/**
 * Builds a multipart FormData payload for creating/updating a PSEA assessment.
 *
 * - Scalar fields and relations go into the `data` JSON blob.
 * - Existing supporting documents (with an `id`) are preserved by referencing
 *   their ids on each core standard component.
 * - Newly added files (no `id`) are appended under
 *   `files.coreStandards[<index>].supportingDocuments` so Strapi uploads and
 *   attaches them to the correct component entry.
 */
const mapCoreStandards = (coreStandards: any[]) =>
  (coreStandards || []).map((cs: any) => ({
    standardNumber: cs.standardNumber,
    // "Not applicable" is carried on its own field, so a standard marked that
    // way sends `meetsStandard: null` alongside `notApplicable: true`.
    meetsStandard:
      cs.notApplicable !== true &&
      (cs.meetsStandard === true || cs.meetsStandard === false)
        ? cs.meetsStandard
        : null,
    notApplicable: cs.notApplicable === true,
    evidence: cs.evidence || [],
    otherSpecify: cs.otherSpecify || "",
    comment: cs.comment || "",
    supportingDocuments: (cs.selectedFiles || [])
      .filter((f: any) => f?.id)
      .map((f: any) => f.id),
  }));

const appendCoreStandardFiles = (formData: FormData, coreStandards: any[]) => {
  (coreStandards || []).forEach((cs: any, index: number) => {
    (cs.selectedFiles || []).forEach((file: any) => {
      if (!file?.id) {
        formData.append(
          `files.coreStandards[${index}].supportingDocuments`,
          file,
          file.name
        );
      }
    });
  });
};

/**
 * `historyDescription` and `justification` are not stored on the assessment
 * itself: the backend strips them off the payload and writes them to the
 * assessment history log instead.
 */
export type PseaHistoryMeta = {
  historyDescription?: string;
  justification?: string;
};

const withHistoryMeta = (data: any, meta?: PseaHistoryMeta) => {
  if (meta?.historyDescription)
    data.historyDescription = meta.historyDescription;
  if (meta?.justification) data.justification = meta.justification;
  return data;
};

export const buildPseaFormData = (
  values: any,
  meta?: PseaHistoryMeta
): FormData => {
  const coreStandards = mapCoreStandards(values.coreStandards);

  const data: any = withHistoryMeta(
    {
      // title is auto-generated on the backend from the contractor name.
      contractor: values.contractor?.value || null,
      agency: values.agency?.value || null,
      dateOfAssessment: values.dateOfAssessment
        ? new Date(values.dateOfAssessment).toLocaleDateString("en-CA")
        : null,
      // overallRating/overallScore are derived from the core standards and
      // computed by the backend, so they are deliberately not sent.
      coreStandards,
    },
    { justification: values.justification, ...meta }
  );

  const formData = new FormData();
  appendCoreStandardFiles(formData, values.coreStandards);
  formData.append("data", JSON.stringify(data));
  return formData;
};
